import layout from './agentLayout.module.css';
import React from 'react';
import { Button, Checkbox, Form, Input, Select, Switch } from 'antd';
import type { FormInstance } from 'antd';
import type { AgentDrawerFormValues, PhoneTree } from '../types';
import {
  MAX_PHONE_TREE_GREETING_LENGTH,
  MAX_PHONE_TREE_OPTIONS,
  PHONE_TREE_KEYS,
  duplicateKeys,
  lowestUnusedKey,
  optionKey,
  repeatKeyCollides,
} from '../phoneTreeKeys';

const REPEAT_KEY_PATTERN = /^[0-9*#]?$/;

export const PhoneTreeSection: React.FC<{
  form: FormInstance<AgentDrawerFormValues>;
  disabled?: boolean;
}> = ({ form, disabled = false }) => {
  const saved: PhoneTree | undefined = Form.useWatch('phone_tree', form);
  const menu: PhoneTree = {
    enabled: saved?.enabled === true,
    options: saved?.options ?? [],
    skip_intro: saved?.skip_intro === true,
    repeat_key: saved?.repeat_key,
    greeting: saved?.greeting,
  };
  const duplicates = duplicateKeys(menu.options);
  const repeatCollides = repeatKeyCollides(menu.options, menu.repeat_key);
  const change = (next: PhoneTree) => form.setFieldValue('phone_tree', next);
  // Pin every option's key before an edit, add or delete so a keyless
  // option's position-derived key cannot shift. Untouched legacy menus stay keyless.
  const pinned = (): PhoneTree['options'] =>
    menu.options.map((item, itemIndex) => ({
      ...item,
      key: optionKey(item, itemIndex),
    }));
  return (
    <div className={layout.menu}>
      <Form.Item
        name='phone_tree'
        style={{ marginBottom: 0 }}
        rules={[
          {
            validator: (_, value: PhoneTree | undefined) => {
              if (value?.enabled && duplicateKeys(value.options).size) {
                return Promise.reject(new Error('Each key can be used once.'));
              }
              if (
                value?.enabled &&
                repeatKeyCollides(value.options, value.repeat_key)
              ) {
                return Promise.reject(
                  new Error(
                    `Key ${value.repeat_key} is already used by an option.`
                  )
                );
              }
              if (
                value?.enabled &&
                (!value.options.length ||
                  value.options.some(
                    (option) => !option.label.trim() || !option.destination
                  ))
              ) {
                return Promise.reject(
                  new Error('Add one to ten keys and assign each to a route.')
                );
              }
              return Promise.resolve();
            },
          },
        ]}
      >
        <Input type='hidden' />
      </Form.Item>
      <div className={layout.menuHeader}>
        <strong>Keypad routing</strong>
        <Switch
          aria-label='Keypad routing'
          checked={menu.enabled}
          disabled={disabled}
          onChange={(enabled) => change({ ...menu, enabled })}
        />
      </div>
      {menu.enabled && (
        <div className={layout.menuBody}>
          <div className={layout.menuLabel}>Press</div>
          {menu.options.map((option, index) => {
            const key = optionKey(option, index);
            return (
              <React.Fragment key={option.id}>
                <div className={layout.menuRow}>
                  <Select
                    value={key}
                    disabled={disabled}
                    status={duplicates.has(key) ? 'error' : undefined}
                    aria-label={`Key for option ${index + 1}`}
                    aria-invalid={duplicates.has(key)}
                    aria-describedby={
                      duplicates.has(key)
                        ? `phone-tree-key-error-${option.id}`
                        : undefined
                    }
                    className={layout.menuKey}
                    options={PHONE_TREE_KEYS.map((digit) => ({
                      value: digit,
                      label: digit,
                    }))}
                    onChange={(next: string) =>
                      change({
                        ...menu,
                        options: pinned().map((item, itemIndex) =>
                          itemIndex === index ? { ...item, key: next } : item
                        ),
                      })
                    }
                  />
                  <Input
                    value={option.label}
                    disabled={disabled}
                    maxLength={120}
                    aria-label={`Key ${index + 1} label`}
                    placeholder='Department or reason'
                    className={layout.menuOption}
                    onChange={(event) =>
                      change({
                        ...menu,
                        options: menu.options.map((item) =>
                          item.id === option.id
                            ? { ...item, label: event.target.value }
                            : item
                        ),
                      })
                    }
                  />
                  {!disabled && (
                    <Button
                      type='text'
                      className={layout.menuDelete}
                      aria-label={`Delete key ${index + 1}`}
                      onClick={() =>
                        change({
                          ...menu,
                          options: pinned().filter(
                            (item) => item.id !== option.id
                          ),
                        })
                      }
                    >
                      <svg
                        width='20'
                        height='16'
                        viewBox='0 0 20 16'
                        fill='none'
                        xmlns='http://www.w3.org/2000/svg'
                        aria-hidden='true'
                        focusable='false'
                      >
                        <path
                          d='M5.99998 12.6667C5.99998 13.4 6.59998 14 7.33331 14H12.6666C13.4 14 14 13.4 14 12.6667V4.66667H5.99998V12.6667ZM14.6666 2.66667H12.3333L11.6666 2H8.33331L7.66665 2.66667H5.33331V4H14.6666V2.66667Z'
                          fill='currentColor'
                        />
                      </svg>
                    </Button>
                  )}
                </div>
                {duplicates.has(key) && (
                  <div
                    role='alert'
                    id={`phone-tree-key-error-${option.id}`}
                    className={layout.menuError}
                  >
                    Key {key} is used more than once. Each key can be used once.
                  </div>
                )}
              </React.Fragment>
            );
          })}
          {!disabled && (
            <Button
              type='text'
              disabled={
                menu.options.length >= MAX_PHONE_TREE_OPTIONS ||
                !lowestUnusedKey(menu.options, menu.repeat_key)
              }
              onClick={() =>
                change({
                  ...menu,
                  options: [
                    ...pinned(),
                    {
                      id: crypto.randomUUID(),
                      label: '',
                      destination: '',
                      key: lowestUnusedKey(menu.options, menu.repeat_key),
                    },
                  ],
                })
              }
            >
              + Add key
            </Button>
          )}
          <Checkbox
            checked={menu.skip_intro}
            disabled={disabled}
            onChange={(event) =>
              change({ ...menu, skip_intro: event.target.checked })
            }
          >
            Skip intro
          </Checkbox>
          <div className={layout.menuField}>
            <label htmlFor='phone-tree-greeting'>Greeting (optional)</label>
            <Input.TextArea
              id='phone-tree-greeting'
              value={menu.greeting ?? ''}
              disabled={disabled}
              maxLength={MAX_PHONE_TREE_GREETING_LENGTH}
              autoSize={{ minRows: 2, maxRows: 6 }}
              aria-describedby='phone-tree-greeting-hint'
              onChange={(event) =>
                change({ ...menu, greeting: event.target.value })
              }
            />
            <div id='phone-tree-greeting-hint' className={layout.menuHint}>
              Spoken before the menu. Ignored when Skip intro is on. Spoken
              exactly as written; variables are not substituted.
            </div>
          </div>
          <div className={layout.menuField}>
            <label htmlFor='phone-tree-repeat-key'>Repeat key (optional)</label>
            <Input
              id='phone-tree-repeat-key'
              value={menu.repeat_key ?? ''}
              disabled={disabled}
              maxLength={1}
              status={repeatCollides ? 'error' : undefined}
              aria-invalid={repeatCollides}
              aria-describedby={
                repeatCollides ? 'phone-tree-repeat-key-error' : undefined
              }
              className={layout.menuRepeatKey}
              onChange={(event) => {
                if (!REPEAT_KEY_PATTERN.test(event.target.value)) return;
                change({ ...menu, repeat_key: event.target.value });
              }}
            />
            {repeatCollides && (
              <div
                role='alert'
                id='phone-tree-repeat-key-error'
                className={layout.menuError}
              >
                Key {menu.repeat_key} is already used by an option.
              </div>
            )}
          </div>
          <div className={layout.menuHint}>
            Assign each key under Route / Escalate when caller.
          </div>
        </div>
      )}
    </div>
  );
};
